import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { switchMap, finalize, catchError } from 'rxjs/operators';

export interface ApiErrorResponse {
  statusCode: number;
  timestamp: string;
  path: string;
  message: string;
}

export interface BCIUploadResponse {
  total: number;
  valid: number;
  invalid: number;
  procesados: {
    success: number;
    failed: number;
    duplicados: number;
  };
  ok: any[];
  duplicados: any[];
  errors: any[];
  sqsErrors: any[];
}

@Injectable({
  providedIn: 'root',
})
export class BciXlsUploadService {
  private apiUrl = environment.apiNest;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private http: HttpClient,
    private cognitoService: CognitoService,
  ) {}

  // Método para obtener los headers con el token
  private async getHeaders(): Promise<HttpHeaders> {
    const session = await this.cognitoService.getSession();
    if (!session || !session.accessToken || !session.accessToken.jwtToken) {
      throw new Error('No se pudo obtener el token de sesión');
    }

    const token = session.accessToken.jwtToken.trim();
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error en el servicio';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      switch (error.status) {
        case 401:
          errorMessage =
            'Error de autenticación. Por favor, inicie sesión nuevamente.';
          break;
        case 403:
          errorMessage = 'No tiene permisos para realizar esta acción.';
          break;
        case 400:
          errorMessage = 'El archivo proporcionado no es válido.';
          break;
        case 404:
          errorMessage = 'El servicio no está disponible en este momento.';
          break;
        case 500:
          errorMessage =
            'Error interno del servidor. Por favor, intente más tarde.';
          break;
      }
    }

    return throwError(() => ({ message: errorMessage, originalError: error }));
  }

  private validateFile(file: File): string | null {
    // Validar tamaño máximo (5MB)
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB en bytes
    if (file.size > MAX_SIZE) {
      return `El archivo excede el tamaño máximo permitido de 5MB. Tamaño actual: ${(file.size / (1024 * 1024)).toFixed(2)}MB`;
    }

    // Validar tipo de archivo
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/octet-stream',
    ];

    if (!validTypes.includes(file.type)) {
      return 'El archivo debe ser un documento Excel válido (.xls o .xlsx)';
    }

    return null;
  }

  uploadXlsFile(file: File, cuenta: string): Observable<BCIUploadResponse> {
    // Validar archivo antes de proceder
    const validationError = this.validateFile(file);
    if (validationError) {
      return throwError(() => ({ message: validationError }));
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('cuenta', cuenta);

    // Activar el loading
    this.loadingSubject.next(true);

    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.post<BCIUploadResponse>(
          `${this.apiUrl}load-transactions-bci-xls/upload`,
          formData,
          { headers },
        );
      }),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
      finalize(() => {
        // Desactivar el loading cuando finalice (ya sea éxito o error)
        this.loadingSubject.next(false);
      }),
    );
  }

  // Método para obtener el estado o resultado de la carga
  getUploadStatus(uploadId: string): Observable<any> {
    return from(this.getHeaders()).pipe(
      switchMap((headers) => {
        return this.http.get(
          `${this.apiUrl}/load-transactions-bci-xls/status/${uploadId}`,
          { headers },
        );
      }),
    );
  }
}
